import { Box, HStack, Text, VStack } from '@gluestack-ui/themed';
import { useContext } from 'react';
import { ProgressBar } from '../ProgressBar';
import { TranslationContext } from '../../../contexts/translations/Translation';
import { COLORS } from '../../../theme/colors';
import type { UserHypothesis } from '../HypothesisSection';

// Reveal step of the verrou — answer-forward: the identification is the hero,
// correctness is a small pill. Reads the 4-head model output on analysis.results:
//   results.gram        : 'pos' | 'neg' | 'uncertain'
//   results.gram_proba  : 0..1
//   results.shape       : 'cocci' | 'bacillus' | 'uncertain'
//   results.shape_proba : 0..1
// Gram is hidden entirely for methylene blue (no Gram reaction).

const AXES: { field: keyof UserHypothesis; labelKey: string; probaKey: string }[] = [
  { field: 'gram', labelKey: 'gram-label', probaKey: 'gram_proba' },
  { field: 'shape', labelKey: 'shape-label', probaKey: 'shape_proba' },
];

const VALUE_LABEL: Record<string, string> = {
  pos: 'gram-pos',
  neg: 'gram-neg',
  cocci: 'shape-cocci',
  bacillus: 'shape-bacillus',
};

interface IHypothesisComparisonProps {
  hypothesis?: UserHypothesis;
  results: Record<string, number | string>;
  coloration?: string;
  testID?: string;
}

export const HypothesisComparison = ({ hypothesis, results, coloration, testID }: IHypothesisComparisonProps) => {
  const { i18n } = useContext(TranslationContext);
  const vb = 'verrou.hypothesis'; // value labels live with the picker
  const b = 'verrou.reveal';

  if (!hypothesis || (!hypothesis.gram && !hypothesis.shape)) return null;

  const showGram = coloration !== 'methylene_blue';
  const axes = AXES.filter((a) => a.field !== 'gram' || showGram);

  const valueLabel = (v?: string) =>
    v && VALUE_LABEL[v] ? i18n?.t(`${vb}.${VALUE_LABEL[v]}`) : undefined;

  return (
    <VStack gap={12}>
      <Text fontWeight="bold" fontSize={16}>
        {i18n?.t(`${b}.title`)}
      </Text>

      {axes.map((axis) => {
        const ai = results[axis.field] as string | undefined;
        const user = hypothesis[axis.field];
        const proba = (results[axis.probaKey] as number) ?? 0;
        const aiKnown = Boolean(ai && ai !== 'uncertain' && ai !== 'INCERTAIN' && VALUE_LABEL[ai]);
        const userCommitted = Boolean(user && user !== 'idk' && VALUE_LABEL[user as string]);
        const correct = aiKnown && userCommitted && user === ai;

        // pill state: uncertain (AI abstains) → amber; else match → teal / mismatch → coral;
        // committed-less (idk / none) → neutral
        let pillBg = COLORS.NEUTRAL[60];
        let pillFg = COLORS.NEUTRAL[90];
        let pillText = i18n?.t(`${b}.abstained`);
        if (!aiKnown) {
          pillBg = '#FAEEDA'; pillFg = '#854F0B'; pillText = i18n?.t(`${b}.uncertain`);
        } else if (userCommitted && correct) {
          pillBg = '#E1F5EE'; pillFg = '#0F6E56'; pillText = i18n?.t(`${b}.match`);
        } else if (userCommitted && !correct) {
          pillBg = '#FAECE7'; pillFg = '#993C1D';
          pillText = i18n?.t(`${b}.mismatch`, { value: valueLabel(user as string) });
        }

        const heroLabel = aiKnown ? valueLabel(ai) : i18n?.t(`${b}.uncertain`);
        const heroColor = aiKnown ? COLORS.TERCIARY[100] : COLORS.NEUTRAL[90];

        return (
          <Box
            key={axis.field}
            testID={`${testID ?? ''}verrou_${axis.field}`}
            style={{
              borderRadius: 12,
              backgroundColor: COLORS.NEUTRAL[0],
              borderWidth: 1,
              borderColor: COLORS.NEUTRAL[70],
              padding: 16,
            }}
          >
            <VStack gap={8}>
              <HStack justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={8}>
                <HStack alignItems="center" gap={10} flexWrap="wrap" style={{ flex: 1 }}>
                  <Text fontSize={13} fontWeight="bold" color={COLORS.NEUTRAL[90]} style={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    {i18n?.t(`${vb}.${axis.labelKey}`)}
                  </Text>
                  <Box style={{ backgroundColor: aiKnown ? '#EEF1F4' : COLORS.NEUTRAL[60], borderRadius: 8, paddingHorizontal: 10, paddingVertical: 3 }}>
                    <Text fontWeight="bold" fontSize={14} color={heroColor}>
                      {heroLabel}
                    </Text>
                  </Box>
                </HStack>
                <Box style={{ backgroundColor: pillBg, borderRadius: 20, paddingHorizontal: 12, paddingVertical: 5 }}>
                  <Text fontSize={12} fontWeight="bold" color={pillFg}>
                    {pillText}
                  </Text>
                </Box>
              </HStack>

              {aiKnown && (
                <HStack alignItems="center" gap={10}>
                  <Box style={{ flex: 1 }}>
                    <ProgressBar progress={proba} />
                  </Box>
                  <Text fontSize={12} color={COLORS.NEUTRAL[90]}>
                    {i18n?.t(`${b}.confidence`, { value: parseFloat((proba * 100).toFixed(1)) })}
                  </Text>
                </HStack>
              )}

              <Text fontSize={12} color={COLORS.NEUTRAL[90]}>
                {i18n?.t(`${b}.your-guess`)}: {userCommitted ? valueLabel(user as string) : i18n?.t(`${b}.not-committed`)}
              </Text>
            </VStack>
          </Box>
        );
      })}
    </VStack>
  );
};
